import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, MapPin, Ruler, Calendar, User, FileText, Plus, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const EngineerProjectForm = ({ open, onClose, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1 = project details, 2 = milestones
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    project_name: '',
    location: '',
    built_up_area: '',
    start_date: '',
    expected_completion_date: '',
    engineer_notes: ''
  });
  const [milestones, setMilestones] = useState([
    { name: '', weightage: '', start_date: '', end_date: '' }
  ]);

  useEffect(() => {
    if (open) {
      fetchCustomers();
      setStep(1);
    }
  }, [open]);

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/users?role=customer');
      setCustomers(res.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.user_id === customerId);
    setFormData({
      ...formData,
      customer_id: customerId,
      customer_name: customer ? customer.name : ''
    });
  };

  const handleMilestoneChange = (index, field, value) => {
    const updated = [...milestones];
    updated[index][field] = value;
    setMilestones(updated);
  };

  const addMilestone = () => {
    setMilestones([...milestones, { name: '', weightage: '', start_date: '', end_date: '' }]);
  };

  const removeMilestone = (index) => {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((_, i) => i !== index));
    }
  };

  const getTotalWeightage = () => {
    return milestones.reduce((sum, m) => sum + (parseFloat(m.weightage) || 0), 0);
  };

  const validateStep1 = () => {
    if (!formData.customer_id || !formData.project_name || !formData.location || 
        !formData.built_up_area || !formData.start_date || !formData.expected_completion_date) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Check if all milestones have required fields
    for (let i = 0; i < milestones.length; i++) {
      const m = milestones[i];
      if (!m.name || !m.weightage || !m.start_date || !m.end_date) {
        toast.error(`Please complete all fields for milestone ${i + 1}`);
        return false;
      }
      if (parseFloat(m.weightage) <= 0 || parseFloat(m.weightage) > 100) {
        toast.error(`Weightage for milestone ${i + 1} must be between 0 and 100`);
        return false;
      }
    }
    
    // Check total weightage
    const total = getTotalWeightage();
    if (Math.abs(total - 100) > 0.1) {
      toast.error(`Total weightage must equal 100%. Current total: ${total.toFixed(1)}%`);
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        built_up_area: parseFloat(formData.built_up_area) || 0,
        milestones: milestones.map(m => ({
          name: m.name,
          weightage: parseFloat(m.weightage),
          start_date: m.start_date,
          end_date: m.end_date
        }))
      };
      
      await api.post('/projects/engineer-submit', submitData);
      toast.success('Project with milestones submitted for admin review!');
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit project');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: '',
      project_name: '',
      location: '',
      built_up_area: '',
      start_date: '',
      expected_completion_date: '',
      engineer_notes: ''
    });
    setMilestones([{ name: '', weightage: '', start_date: '', end_date: '' }]);
    setStep(1);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const totalWeightage = getTotalWeightage();
  const isWeightageValid = Math.abs(totalWeightage - 100) <= 0.1;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-white border-0 shadow-2xl rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">
            {step === 1 ? 'Project Details' : 'Define Milestones'}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            {step === 1 
              ? 'Enter the project information' 
              : 'Add custom milestones with percentage weightage'}
          </DialogDescription>
          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-4">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${step >= 1 ? 'bg-blue-900 text-white' : 'bg-stone-200 text-stone-500'}`}>1</div>
            <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-900' : 'bg-stone-200'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${step >= 2 ? 'bg-blue-900 text-white' : 'bg-stone-200 text-stone-500'}`}>2</div>
          </div>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 pt-2" data-testid="engineer-project-form">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <User className="h-4 w-4 mr-2 text-stone-400" />
                Select Customer *
              </Label>
              <Select
                value={formData.customer_id}
                onValueChange={handleCustomerSelect}
              >
                <SelectTrigger className="h-11 border-stone-200 rounded-lg" data-testid="customer-select">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customer => (
                    <SelectItem key={customer.user_id} value={customer.user_id}>
                      {customer.name} ({customer.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <Building2 className="h-4 w-4 mr-2 text-stone-400" />
                Project Name *
              </Label>
              <Input
                value={formData.project_name}
                onChange={(e) => handleChange('project_name', e.target.value)}
                placeholder="e.g., Villa Construction - Phase 1"
                className="h-11 border-stone-200 rounded-lg"
                data-testid="project-name-input"
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-stone-400" />
                Location / Address *
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Full address of the site"
                className="h-11 border-stone-200 rounded-lg"
                data-testid="location-input"
              />
            </div>

            {/* Built-up Area */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <Ruler className="h-4 w-4 mr-2 text-stone-400" />
                Built-up Area (sq.ft) *
              </Label>
              <Input
                type="number"
                value={formData.built_up_area}
                onChange={(e) => handleChange('built_up_area', e.target.value)}
                placeholder="e.g., 3500"
                className="h-11 border-stone-200 rounded-lg"
                data-testid="built-up-area-input"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-stone-400" />
                  Start Date *
                </Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  className="h-11 border-stone-200 rounded-lg"
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700">
                  Expected Completion *
                </Label>
                <Input
                  type="date"
                  value={formData.expected_completion_date}
                  onChange={(e) => handleChange('expected_completion_date', e.target.value)}
                  className="h-11 border-stone-200 rounded-lg"
                  data-testid="completion-date-input"
                />
              </div>
            </div>

            {/* Engineer Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-stone-400" />
                Notes for Admin
              </Label>
              <Textarea
                value={formData.engineer_notes}
                onChange={(e) => handleChange('engineer_notes', e.target.value)}
                placeholder="Any additional details about the project..."
                className="min-h-[80px] border-stone-200 rounded-lg"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4 border-t border-stone-100">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                className="flex-1 h-11 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white rounded-lg"
              >
                Next: Add Milestones
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2" data-testid="milestone-form">
            {/* Weightage indicator */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${isWeightageValid ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
              <div className="flex items-center gap-2">
                {isWeightageValid ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                )}
                <span className={`font-medium ${isWeightageValid ? 'text-green-800' : 'text-orange-800'}`}>
                  Total Weightage: {totalWeightage.toFixed(1)}%
                </span>
              </div>
              <span className={`text-sm ${isWeightageValid ? 'text-green-600' : 'text-orange-600'}`}>
                {isWeightageValid ? 'Valid' : 'Must equal 100%'}
              </span>
            </div>

            {/* Milestones List */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              {milestones.map((milestone, index) => (
                <div key={index} className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-stone-700">Milestone {index + 1}</span>
                    {milestones.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMilestone(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs text-stone-500">Milestone Name *</Label>
                      <Input
                        value={milestone.name}
                        onChange={(e) => handleMilestoneChange(index, 'name', e.target.value)}
                        placeholder="e.g., Foundation Work, RCC Slab 1st Floor"
                        className="h-10 border-stone-200 rounded-lg mt-1"
                        data-testid={`milestone-name-${index}`}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-stone-500">Weightage (%) *</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={milestone.weightage}
                        onChange={(e) => handleMilestoneChange(index, 'weightage', e.target.value)}
                        placeholder="e.g., 15"
                        className="h-10 border-stone-200 rounded-lg mt-1"
                        data-testid={`milestone-weightage-${index}`}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-stone-500">Start Date *</Label>
                        <Input
                          type="date"
                          value={milestone.start_date}
                          onChange={(e) => handleMilestoneChange(index, 'start_date', e.target.value)}
                          className="h-10 border-stone-200 rounded-lg mt-1 text-xs"
                          data-testid={`milestone-start-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">End Date *</Label>
                        <Input
                          type="date"
                          value={milestone.end_date}
                          onChange={(e) => handleMilestoneChange(index, 'end_date', e.target.value)}
                          className="h-10 border-stone-200 rounded-lg mt-1 text-xs"
                          data-testid={`milestone-end-${index}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Milestone Button */}
            <Button
              type="button"
              variant="outline"
              onClick={addMilestone}
              className="w-full h-10 border-dashed border-stone-300 text-stone-600 hover:bg-stone-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Milestone
            </Button>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Each milestone's weightage represents its contribution to the overall project completion percentage. 
                The total must equal 100%.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4 border-t border-stone-100">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !isWeightageValid}
                className="flex-1 h-11 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white rounded-lg disabled:opacity-50"
                data-testid="submit-project-button"
              >
                {submitting ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting...
                  </span>
                ) : (
                  'Submit for Review'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EngineerProjectForm;
